import { Rect, RectConfig } from 'konva/lib/shapes/Rect';
import { Shape, ShapeType } from '../shape';
import { Transformer, TransformerConfig } from 'konva/lib/shapes/Transformer';
import { KonvaUtils } from './konva.utils';
import { getArcLineHeight } from '@/watch-renderer/utils/string.utilts';
import { useEditorStore } from '@/store/use-editor-store';
import { useImagesStore } from '@/store/use-images-store';

export class KonvaLayout {
  // rect style
  private rectFill = import.meta.env.DEV ? 'rgba(0,0,0,0)' : 'rgba(0,0,0,0)';

  private transformerStroke = 'rgba(13, 132, 242 ,1)';

  drawShape = (shape: Shape, parentShape?: Shape) => {
    const { id, type, attrs } = shape;
    const shapeName = id;
    const {
      x = 0,
      y = 0,
      w = 100,
      h = 100,
      rotation = 0,
      align = 'left',
      sizeContent,
      fillType,
      radius: textRadius = 0,
      style: textStyle = 'normal',
      verticalAlign = 'top',
      _visibility,
      pivotX,
      pivotY
    } = { ...attrs };

    const runtime = KonvaUtils.getRuntimData(id);
    // runtime 的 width/height 已是 watch-renderer 按 _scale（全局×元素）算出的内容尺寸，边框直接沿用，与内容一致
    // 元素是否在组内
    const parentType = parentShape?.type;
    const isInGroup = parentType == ShapeType.Widget;
    const isInSlot = parentType == ShapeType.Slot;

    const baseAttrs: RectConfig = {
      // id必须=shape
      id: 'shape',
      name: shapeName,
      fill: this.rectFill,
      x: isInGroup ? runtime?.offsetX ?? x : x,
      y: isInGroup ? runtime?.offsetY ?? y : y,
      // 默认设置所有元素均可拖拽，具体是否拖拽在拖拽函数中判断
      draggable: true,
      width: w,
      height: h,
      rotation,
      visible: !!_visibility
    };
    // 进度条成组后 attrs 为 (0,0)，用 attrs 绘制使边框能包住；否则会用 runtime.offsetX/offsetY 导致超出组边框
    if (type === ShapeType.ProgressBar && isInGroup) {
      // 动效组子元素渲染在左上角，x/y 用 0
      const isInConditionGroup = (parentShape?.attrs as any)?.widgetConditions?.length > 0;
      baseAttrs.x = isInConditionGroup ? 0 : x;
      baseAttrs.y = isInConditionGroup ? 0 : y;
    }

    // 组仅含一直线进度条时：表盘画布用 selfOffset 绘制，内容在 (x-offsetX, y-offsetY)；把组的边框 Rect 画到该位置才能包住内容，Group 仍用 (x,y) 放子元素
    if (type === ShapeType.Widget && shape.children?.length === 1) {
      const kid = shape.children[0];
      if (
        kid?.type === ShapeType.ProgressBar &&
        kid.attrs.progressBarType === 'line'
      ) {
        const imageStore = useImagesStore();
        const colorId =
          useEditorStore().themeColor?.id ??
          Object.keys(
            kid.attrs.resources?.progress ??
              kid.attrs.resources?.indicatorImage ??
              {}
          )[0];
        let wInd = 0;
        let hInd = 0;
        let hProgress = 0;
        if (colorId) {
          const ind = kid.getImageSize('indicatorImage', colorId);
          const prog = kid.getImageSize('progress', colorId);
          wInd = ind?.width ?? 0;
          hInd = ind?.height ?? 0;
          hProgress = prog?.height ?? 0;
          if (!wInd || !hInd) {
            const res = kid.attrs.resources?.indicatorImage;
            if (res && typeof res === 'object') {
              Object.values(res).forEach((srcs) => {
                const arr = Array.isArray(srcs) ? srcs : [srcs];
                arr.forEach((src) => {
                  if (!src) return;
                  const item =
                    imageStore.imagesMap[src] ??
                    imageStore.imagesMap[
                      /[/\\]/.test(src) ? src.replace(/^.*[/\\]/, '') : src
                    ];
                  if (item?.width != null)
                    wInd = Math.max(wInd, item.width ?? 0);
                  if (item?.height != null)
                    hInd = Math.max(hInd, item.height ?? 0);
                });
              });
            }
          }
          if (hProgress === 0 && kid.attrs.resources?.progress) {
            const res = kid.attrs.resources.progress;
            Object.values(res).forEach((srcs) => {
              const arr = Array.isArray(srcs) ? srcs : [srcs];
              arr.forEach((src) => {
                if (!src) return;
                const item =
                  imageStore.imagesMap[src] ??
                  imageStore.imagesMap[
                    /[/\\]/.test(src) ? src.replace(/^.*[/\\]/, '') : src
                  ];
                if (item?.height != null)
                  hProgress = Math.max(hProgress, item.height ?? 0);
              });
            });
          }
        }
        const offsetX = wInd / 2;
        const offsetY = hInd > hProgress ? (hInd - hProgress) / 2 : 0;
        baseAttrs.x = (x ?? 0) - offsetX;
        baseAttrs.y = (y ?? 0) - offsetY;
        baseAttrs.data = {
          ...(baseAttrs.data as object),
          progressBarGroupLogicalPosition: { x: x ?? 0, y: y ?? 0 },
          progressBarGroupOffset: { x: offsetX, y: offsetY }
        };
      }
    }

    // 如果是指针，则需要根据runtime值设置旋转角度
    if (type == ShapeType.Image) {
      baseAttrs.rotation = runtime?.rotation ?? 0;
      // 使用实际渲染宽高计算 offset，确保与拖拽、setAttrs 逻辑一致
      const actualSize = KonvaUtils.getImageActualSize(shape);
      const offsetX = pivotX !== undefined ? Number(pivotX) : actualSize.width / 2;
      const offsetY = pivotY !== undefined ? Number(pivotY) : actualSize.height / 2;
      baseAttrs.offset = {
        x: offsetX,
        y: offsetY
      };
      baseAttrs.x = (baseAttrs.x ?? 0) + offsetX;
      baseAttrs.y = (baseAttrs.y ?? 0) + offsetY;
      // 图片类：边框与内容一致，直接用 runtime 尺寸（runtime 已是按 _scale 缩放后的内容尺寸）
      baseAttrs.width = runtime?.width ?? w;
      baseAttrs.height = runtime?.height ?? h;
    }

    // 文本
    if (type == ShapeType.Text || type == ShapeType.TextWatchface) {
      if (textStyle == 'arc') {
        // 弧形进度条
        const lineHeight = runtime?.lineHeight ?? 0;
        const _lineHeight = getArcLineHeight(lineHeight, verticalAlign);
        baseAttrs.width = baseAttrs.height = (textRadius + _lineHeight) * 2;
        baseAttrs.rotation = 0;
      } else {
        // 文本类型时且自适应宽度时，需要设置画布的宽度
        if (sizeContent) {
          baseAttrs.width = runtime?.width ?? baseAttrs.width;
          baseAttrs.height = runtime?.height ?? baseAttrs.height;
        }
        if (rotation) {
          const offsetX = align == 'center' ? w / 2 : align == 'right' ? w : 0;
          baseAttrs.offsetX = offsetX;
          baseAttrs.x = (baseAttrs.x ?? 0) + offsetX;
        }
      }
    }

    // 图组类型：边框与内容一致，直接用 runtime 尺寸
    if (type == ShapeType.ImageArray) {
      baseAttrs.width = runtime?.width ?? baseAttrs.width ?? w;
      baseAttrs.height = runtime?.height ?? baseAttrs.height ?? h;

      // 数字类型图组的对齐方式
      if (fillType == '数字填充' && !shape.widgetImageArray) {
        const offsetX =
          (baseAttrs.width ?? 0) *
          (align == 'center' ? 0.5 : align == 'right' ? 1 : 0);
        baseAttrs.offsetX = offsetX;
      }
    }

    // 容器
    if (type == ShapeType.Slot) {
      baseAttrs.width = runtime?.width ?? baseAttrs.width;
      baseAttrs.height = runtime?.height ?? baseAttrs.height;

      const { w = 0, h = 0 } = KonvaUtils.getRuntimeSlotSize(shape) ?? {};
      baseAttrs.width = w;
      baseAttrs.height = h;
      if (shape.attrs.movable) {
        const { curSlotPosition, positions } = shape.attrs;
        if (curSlotPosition && positions && positions.length > 1) {
          baseAttrs.x = positions[curSlotPosition].x ?? 0;
          baseAttrs.y = positions[curSlotPosition].y ?? 0;
        }
      }
    }
    if (isInSlot) {
      baseAttrs.x = 0;
      baseAttrs.y = 0;
    }

    // 组（Widget）的边框需要跟随子元素实际渲染尺寸，
    // 当子元素绑定数据源或缩放后，runtime 尺寸可能与 attrs.w/h 不同
    if (type === ShapeType.Widget && runtime?.width && runtime?.height) {
      const _beforeW = baseAttrs.width;
      const _beforeH = baseAttrs.height;
      baseAttrs.width = Math.max(runtime.width, baseAttrs.width ?? 0);
      baseAttrs.height = Math.max(runtime.height, baseAttrs.height ?? 0);
      console.log(`[ALIGN-DEBUG] drawShape Widget | name=${attrs.name} attrs.w=${w} attrs.h=${h} runtime.w=${runtime.width} runtime.h=${runtime.height} before={${_beforeW},${_beforeH}} final={${baseAttrs.width},${baseAttrs.height}}`);
    }

    // 如果是sprite需要根据显示的元素设置宽度和高度
    if (type == ShapeType.Sprite) {
      const spriteItem = shape.getSpriteMulitItems(true)?.[0];
      baseAttrs.width = spriteItem?.w;
      baseAttrs.height = spriteItem?.h;
    }

    const rect = new Rect({
      ...baseAttrs,
      _name: shape.attrs.name
    });

    // 如果只有位置变化，则设置为postion，提高性能
    if (!baseAttrs.rotation && !shape.resizeEnabled())
      rect.transformsEnabled('position');

    return rect;
  };

  drawTransformer(disableTransform?: boolean) {
    // eslint-disable-next-line prefer-const
    let tr: Transformer | undefined;
    // 是否可鼠标拖拽尺寸，在select时进行判断
    const option: TransformerConfig = {
      anchorSize: 6,
      rotateAnchorOffset: 24,
      borderStroke: this.transformerStroke,
      anchorStroke: this.transformerStroke,
      // rotationSnaps: [0, 90, -90, -180],
      keepRatio: false,
      rotateEnabled: false,
      flipEnabled: false,
      // shouldOverdrawWholeArea: false,
      boundBoxFunc: (oldBoundBox, newBoundBox) => {
        let data = tr?.getAttr('data') ?? {};
        if (!data.active) {
          data = { ...data, active: tr?.getActiveAnchor() };
          tr?.setAttr('data', {
            ...data
          });
        }
        if (data.active !== tr?.getActiveAnchor()) {
          return oldBoundBox;
        }

        return newBoundBox;
      }
    };
    if (disableTransform) option['enabledAnchors'] = [];

    tr = new Transformer(option);
    tr?.on('transformend', () => {
      tr?.setAttr('data', {
        ...tr.getAttr('data'),
        active: undefined
      });
    });
    return tr;
  }
}
